import { Box, Typography } from "@mui/material";
import { useCallback, useEffect, useRef } from "react";

import { uiNeutrals } from "../../constants/uiNeutrals";

/** Split leading emoji / glyph + caption (e.g. "🍽️ Meals", "✨ Anything"). */
export const cuisineSplitLabel = (label) => {
  if (!label) return { visual: "✨", caption: "" };
  const trimmed = label.trim();
  if (trimmed === "Any cuisine") return { visual: "✨", caption: "Any cuisine" };
  const space = trimmed.indexOf(" ");
  if (space === -1) return { visual: "✨", caption: trimmed };
  return {
    visual: trimmed.slice(0, space).trimEnd(),
    caption: trimmed.slice(space + 1).trim(),
  };
};

const HeroCuisineScroll = ({
  items,
  selectedIndex,
  onSelectedIndexChange,
  ariaLabel,
  rowHeightPx = 76,
  visibleRows = 5,
}) => {
  const rowCount = visibleRows;
  const wheelHeightPx = rowCount * rowHeightPx;
  const padY = (wheelHeightPx - rowHeightPx) / 2;
  const edgeFadePx = Math.round(rowHeightPx * 0.55);
  const wheelMaskGradient = `linear-gradient(to bottom, transparent 0, #000 ${edgeFadePx}px, #000 calc(100% - ${edgeFadePx}px), transparent 100%)`;

  const scrollRef = useRef(null);
  const settleTimerRef = useRef(null);
  /** Idle after scroll stops (no scrollTo — avoids fighting iOS / touch momentum vs snap). */
  const SYNC_IDLE_MS = 145;

  const syncSelectionFromScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || !items.length) return;
    const raw = el.scrollTop / rowHeightPx;
    const next = Math.min(
      items.length - 1,
      Math.max(0, Math.round(raw)),
    );
    if (next !== selectedIndex) onSelectedIndexChange(next);
  }, [items.length, onSelectedIndexChange, selectedIndex, rowHeightPx]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !items.length) return undefined;
    const onScrollIdle = () => {
      if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
      settleTimerRef.current = setTimeout(syncSelectionFromScroll, SYNC_IDLE_MS);
    };
    const onScrollEnd = () => {
      if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
      settleTimerRef.current = null;
      syncSelectionFromScroll();
    };
    el.addEventListener("scroll", onScrollIdle, { passive: true });
    el.addEventListener("scrollend", onScrollEnd);
    return () => {
      el.removeEventListener("scroll", onScrollIdle);
      el.removeEventListener("scrollend", onScrollEnd);
      if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
    };
  }, [items.length, syncSelectionFromScroll]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !items.length) return;
    const target = selectedIndex * rowHeightPx;
    if (Math.abs(el.scrollTop - target) > 2) {
      el.scrollTo({ top: target, behavior: "auto" });
    }
  }, [selectedIndex, items.length, rowHeightPx]);

  return (
    <Box
      sx={{
        position: "relative",
        height: wheelHeightPx,
        minHeight: wheelHeightPx,
        minWidth: 0,
        width: "100%",
        bgcolor: "transparent",
        overflow: "hidden",
        maskImage: wheelMaskGradient,
        WebkitMaskImage: wheelMaskGradient,
        maskSize: "100% 100%",
        WebkitMaskSize: "100% 100%",
        maskRepeat: "no-repeat",
        WebkitMaskRepeat: "no-repeat",
      }}
      role="listbox"
      aria-label={ariaLabel}
    >
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          top: `${padY}px`,
          left: 4,
          right: 4,
          height: rowHeightPx,
          borderRadius: 2,
          bgcolor: uiNeutrals.surface,
          pointerEvents: "none",
          zIndex: 1,
        }}
      />
      <Box
        ref={scrollRef}
        sx={{
          position: "relative",
          zIndex: 2,
          height: "100%",
          overflowY: "auto",
          overflowX: "hidden",
          bgcolor: "transparent",
          scrollSnapType: "y mandatory",
          WebkitOverflowScrolling: "touch",
          touchAction: "pan-y",
          overscrollBehavior: "contain",
          boxSizing: "border-box",
          "&::-webkit-scrollbar": { display: "none" },
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        <Box sx={{ pt: `${padY}px`, pb: `${padY}px` }}>
          {items.map((item, i) => {
            const { visual, caption } = cuisineSplitLabel(item.label);
            const selected = i === selectedIndex;
            return (
              <Box
                key={item.id}
                role="option"
                aria-selected={selected}
                sx={{
                  height: rowHeightPx,
                  scrollSnapAlign: "center",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  px: 1,
                  flexShrink: 0,
                  borderRadius: 2,
                  mx: 0.5,
                  bgcolor: "transparent",
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    fontSize: "1.875rem",
                    lineHeight: 1,
                  }}
                  aria-hidden
                >
                  {visual}
                </Typography>
                <Typography
                  sx={{
                    mt: 0.35,
                    fontSize: "0.8125rem",
                    fontWeight: selected ? 600 : 450,
                    color: selected ? "#111827" : "#888",
                    textAlign: "center",
                    lineHeight: 1.2,
                    maxWidth: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {caption}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default HeroCuisineScroll;
